// src/views/v2Playground/V2Playground.tsx
//
// A dev-only tool for exercising the v2 endpoints (functions/v2/endpoints/*)
// over real HTTP, against the standalone local server
// (functions: `npm run serve:v2`, see functions/v2/server.ts) and the
// Firestore/Auth emulators. Deliberately isolated from every real screen —
// nothing here reads or writes through the app's normal Redux
// actions/slices, and it never touches v1 endpoints or the real Firebase
// project. Safe to delete this whole folder without affecting anything
// else once v2 testing is done.
//
// Auth: the v2 server verifies tokens against the local Auth EMULATOR
// (FIREBASE_AUTH_EMULATOR_HOST), which is a completely separate identity
// store from the real Firebase project this dashboard normally logs into —
// the admin's real session token won't verify there. So this page mints
// its own throwaway emulator user via the Auth emulator's REST API
// directly (no SDK, just fetch) rather than reusing `src/Firebase.js`'s
// real Firebase Auth instance.
import React, { useMemo, useState } from "react";

const V2_ROUTES = [
  "createLicenseCode",
  "createReferralCode",
  "createWhiteLabelReferralCode",
  "createInviteCode",
  "getAccessCodeByCode",
  "listAccessCodes",
  "useAccessCode",
  "createContract",
  "getContractById",
  "listContracts",
  "createBusiness",
  "updateBusiness",
  "getBusinessById",
  "getBusinessByCode",
  "listBusinesses",
  "createPartnerClientId",
  "updatePartnerClientId",
  "deletePartnerClientId",
  "cancelPartnerClientId",
  "getPartnerClientIdById",
  "createClientUser",
  "createAdvisorUser",
  "getUserById",
  "getUserByAppId",
  "updateUser",
  "listUsers",
  "linkClientAdvisors",
  "unlinkClientAdvisors",
  "getSharingAssignmentsForOwner",
  "getSharingAssignmentsForGuest",
  "signup",
  "signupWithPartnerClientId",
] as const;

const PUBLIC_ROUTES = new Set(["signup", "signupWithPartnerClientId"]);

// Pre-filled examples so you're not guessing field names from scratch —
// see the matching endpoint file in functions/v2/endpoints/ for the exact
// shape each one expects. `code` is deliberately absent from every create*
// template below: AccessCode.create() always sets it to the generated id
// (ignoring anything you pass), so there's nothing to fill in — check the
// Data Browser's Access Codes table after creating one to get its code
// (== its id) for use in useAccessCode/signup/signupWithPartnerClientId.
const BODY_TEMPLATES: Record<string, object> = {
  createLicenseCode: {
    isActive: true,
    role_type: "client",
    contract_ref: "<contract id>",
    business_client: "<business id>",
  },
  createReferralCode: {
    isActive: true,
    useLimit: 10,
    role_type: "client",
    business_client: "<business id>",
    for_business_client: "Acme Wealth",
  },
  createWhiteLabelReferralCode: {
    isActive: true,
    useLimit: 5,
    role_type: "client",
    contract_ref: "<contract id>",
    business_client: "<business id>",
  },
  createInviteCode: { isActive: true, role_type: "advisor", generatedByUserId: "admin-1" },
  useAccessCode: { code: "<access code, from the Data Browser's Access Codes table>", userId: "<user id>" },
  createContract: {
    owner: "<business id>",
    reference: "CONTRACT-1",
    use_limit: 100,
    expire_at: new Date(Date.now() + 365 * 86_400_000).toISOString(),
  },
  // type: "partner" | "distributer" | "owner" — "owner" is for bootstrapping
  // the one elisyan business (see cancelPartnerClientId), not a per-partner type.
  createBusiness: {
    name: "Acme Wealth",
    type: "partner",
    enable_advisors: true,
    need_client_id: false,
    need_admin_dashboard: false,
  },
  updateBusiness: { id: "<business id>", status: "active" },
  createPartnerClientId: { clientId: "ACME-HR", type: "business", business: "<business id>", users_limit: 10 },
  updatePartnerClientId: { id: "<partnerClientId id>", users_limit: 25 },
  deletePartnerClientId: { id: "<partnerClientId id>" },
  // "Reset to elisyan": cancels this partnerClientId, migrates every user
  // who signed up through it back to 'elisyan', unassigns their advisors,
  // and cancels+links their old access codes to a new replacement referral
  // code. Requires a business with code ELISYAN to already exist.
  cancelPartnerClientId: { id: "<partnerClientId id>", adminUuid: "<your admin uid>" },
  createClientUser: { email: "c@example.com", user_name: "Client", app_user_id: "app-c1", business_name: "elisyan" },
  createAdvisorUser: {
    email: "a@example.com",
    user_name: "Advisor",
    app_user_id: "app-a1",
    business_name: "elisyan",
    roles: ["CRM"],
  },
  updateUser: { id: "<user id>", user_name: "Updated Name" },
  // Batch shape — one request can link/unlink multiple pairs at once.
  // Each link writes advisors[]/clients[]/sharing[]/shared[] together (and
  // fires the AWS grant) in one atomic step per pair; `permission` is
  // optional per pair and defaults to [{permissions: ["read","write"]}] —
  // matches v1's real sharing[]/shared[] shape exactly (an array of
  // {permissions: string[]} objects, not a flat string array).
  linkClientAdvisors: {
    links: [
      { clientId: "<client user id>", advisorId: "<advisor user id>", permission: [{ permissions: ["read", "write"] }] },
    ],
  },
  unlinkClientAdvisors: { unlinks: [{ clientId: "<client user id>", advisorId: "<advisor user id>" }] },
  signup: {
    code: "<access code, from the Data Browser's Access Codes table>",
    app_user_id: "app-1",
    email: "new@example.com",
    user_name: "New User",
  },
  signupWithPartnerClientId: {
    code: "<access code, from the Data Browser's Access Codes table>",
    app_user_id: "app-1",
    email: "u@example.com",
    user_name: "U",
    clientId: "ACME-HR",
    businessCode: "ACME_WEALTH",
  },
};

const QUERY_TEMPLATES: Record<string, object> = {
  getAccessCodeByCode: { code: "<access code, from the Data Browser's Access Codes table>" },
  getContractById: { id: "<contract id>" },
  getBusinessById: { id: "<business id>" },
  getBusinessByCode: { code: "ACME_WEALTH" },
  getPartnerClientIdById: { id: "<partnerClientId id>" },
  getUserById: { id: "<user id>" },
  getUserByAppId: { app_user_id: "<app_user_id>" },
  getSharingAssignmentsForOwner: { ownerId: "<client user id>" },
  getSharingAssignmentsForGuest: { guestId: "<advisor user id>" },
};

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

interface DataRow {
  id: string;
  [key: string]: unknown;
}

async function fetchList(serverUrl: string, token: string, route: string): Promise<DataRow[]> {
  const res = await fetch(`${serverUrl}/${route}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: "{}",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status} from ${route}`);
  return data.data as DataRow[];
}

const DataTable: React.FC<{ rows: DataRow[]; columns: { key: string; label: string }[] }> = ({ rows, columns }) => (
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
    <thead>
      <tr>
        {columns.map((c) => (
          <th key={c.key} style={{ textAlign: "left", borderBottom: "1px solid #ccc", padding: 4 }}>
            {c.label}
          </th>
        ))}
        <th />
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.id}>
          {columns.map((c) => (
            <td
              key={c.key}
              title={String(row[c.key] ?? "")}
              style={{
                padding: 4,
                borderBottom: "1px solid #eee",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {String(row[c.key] ?? "")}
            </td>
          ))}
          <td style={{ padding: 4 }}>
            <button onClick={() => navigator.clipboard?.writeText(row.id)}>copy id</button>
          </td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={columns.length + 1} style={{ padding: 4, color: "#999" }}>
            (none)
          </td>
        </tr>
      )}
    </tbody>
  </table>
);

const V2Playground: React.FC = () => {
  const [serverUrl, setServerUrl] = useState("http://127.0.0.1:5050");
  const [authEmulatorUrl, setAuthEmulatorUrl] = useState("http://127.0.0.1:9099");
  const [testEmail, setTestEmail] = useState("v2-playground-tester@example.com");
  const [testPassword, setTestPassword] = useState("password123");
  const [token, setToken] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<string>("No token yet");

  const [route, setRoute] = useState<string>(V2_ROUTES[0]);
  const [queryText, setQueryText] = useState(prettyJson(QUERY_TEMPLATES[V2_ROUTES[0]] ?? {}));
  const [bodyText, setBodyText] = useState(prettyJson(BODY_TEMPLATES[V2_ROUTES[0]] ?? {}));
  const [includeAuth, setIncludeAuth] = useState(!PUBLIC_ROUTES.has(V2_ROUTES[0]));

  const [responseStatus, setResponseStatus] = useState<number | null>(null);
  const [responseBody, setResponseBody] = useState<string>("");
  const [sending, setSending] = useState(false);

  const [businesses, setBusinesses] = useState<DataRow[]>([]);
  const [contracts, setContracts] = useState<DataRow[]>([]);
  const [users, setUsers] = useState<DataRow[]>([]);
  const [accessCodes, setAccessCodes] = useState<DataRow[]>([]);
  const [browserStatus, setBrowserStatus] = useState("Not loaded yet — click Refresh below.");

  const parsedQuery = useMemo(() => {
    try {
      return JSON.parse(queryText || "{}");
    } catch {
      return null;
    }
  }, [queryText]);

  function handleRouteChange(next: string) {
    setRoute(next);
    setQueryText(prettyJson(QUERY_TEMPLATES[next] ?? {}));
    setBodyText(prettyJson(BODY_TEMPLATES[next] ?? {}));
    setIncludeAuth(!PUBLIC_ROUTES.has(next));
    setResponseStatus(null);
    setResponseBody("");
  }

  async function mintTestToken() {
    setTokenStatus("Signing up/in against the Auth emulator...");
    try {
      // signUp works even for an existing email against the Auth emulator
      // (it just errors EMAIL_EXISTS) — fall back to signIn in that case.
      const attempt = async (endpoint: string) =>
        fetch(`${authEmulatorUrl}/identitytoolkit.googleapis.com/v1/accounts:${endpoint}?key=fake-api-key`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: testEmail, password: testPassword, returnSecureToken: true }),
        });

      let res = await attempt("signUp");
      let data = await res.json();
      if (data.error?.message === "EMAIL_EXISTS") {
        res = await attempt("signInWithPassword");
        data = await res.json();
      }

      if (!res.ok || !data.idToken) {
        setTokenStatus(`Failed: ${data.error?.message || res.statusText}`);
        return;
      }

      setToken(data.idToken);
      setTokenStatus(`Got token for ${testEmail} (uid: ${data.localId})`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setTokenStatus(`Failed: ${message} — is the Auth emulator running at ${authEmulatorUrl}?`);
    }
  }

  async function sendRequest() {
    setSending(true);
    setResponseStatus(null);
    setResponseBody("");
    try {
      let body: unknown = {};
      try {
        body = JSON.parse(bodyText || "{}");
      } catch {
        setResponseBody("Body is not valid JSON");
        setSending(false);
        return;
      }

      const qs =
        parsedQuery && Object.keys(parsedQuery).length
          ? "?" + new URLSearchParams(parsedQuery as Record<string, string>).toString()
          : "";

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (includeAuth) {
        if (!token) {
          setResponseBody("No token — click \"Get test token\" first (or uncheck \"Send Authorization header\").");
          setSending(false);
          return;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${serverUrl}/${route}${qs}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const text = await res.text();
      setResponseStatus(res.status);
      try {
        setResponseBody(prettyJson(JSON.parse(text)));
      } catch {
        setResponseBody(text);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setResponseBody(`Request failed: ${message} — is the v2 server running at ${serverUrl}? (npm run serve:v2)`);
    } finally {
      setSending(false);
    }
  }

  async function refreshDataBrowser() {
    if (!token) {
      setBrowserStatus('No token — click "Get test token" above first.');
      return;
    }
    setBrowserStatus("Loading...");
    try {
      const [b, c, u, a] = await Promise.all([
        fetchList(serverUrl, token, "listBusinesses"),
        fetchList(serverUrl, token, "listContracts"),
        fetchList(serverUrl, token, "listUsers"),
        fetchList(serverUrl, token, "listAccessCodes"),
      ]);
      setBusinesses(b);
      setContracts(c);
      setUsers(u);
      setAccessCodes(a);
      setBrowserStatus(
        `Loaded ${b.length} businesses, ${c.length} contracts, ${u.length} users, ${a.length} access codes.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setBrowserStatus(`Failed: ${message}`);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 1000, fontFamily: "monospace" }}>
      <h2>v2 Endpoint Playground</h2>
      <p style={{ color: "#666" }}>
        Dev-only tool. Requires <code>npm run serve:v2</code> running in <code>functions/</code>, and the Firestore
        + Auth emulators (<code>firebase emulators:start --only firestore,auth</code>).
      </p>

      <fieldset style={{ marginBottom: 16 }}>
        <legend>Servers</legend>
        <label>
          v2 server URL:{" "}
          <input value={serverUrl} onChange={(e) => setServerUrl(e.target.value)} style={{ width: 260 }} />
        </label>
        <br />
        <label>
          Auth emulator URL:{" "}
          <input value={authEmulatorUrl} onChange={(e) => setAuthEmulatorUrl(e.target.value)} style={{ width: 260 }} />
        </label>
      </fieldset>

      <fieldset style={{ marginBottom: 16 }}>
        <legend>Test auth token (Auth emulator only — not your real login)</legend>
        <label>
          Email: <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} style={{ width: 220 }} />
        </label>{" "}
        <label>
          Password:{" "}
          <input
            value={testPassword}
            onChange={(e) => setTestPassword(e.target.value)}
            style={{ width: 140 }}
            type="password"
          />
        </label>{" "}
        <button onClick={mintTestToken}>Get test token</button>
        <div style={{ marginTop: 4 }}>{tokenStatus}</div>
      </fieldset>

      <fieldset style={{ marginBottom: 16 }}>
        <legend>Request</legend>
        <label>
          Endpoint:{" "}
          <select value={route} onChange={(e) => handleRouteChange(e.target.value)}>
            {V2_ROUTES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>{" "}
        <label>
          <input type="checkbox" checked={includeAuth} onChange={(e) => setIncludeAuth(e.target.checked)} />{" "}
          Send Authorization header
        </label>
        <br />
        <br />
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div>Query (JSON object, sent as ?params)</div>
            <textarea
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              rows={8}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div>Body (JSON object)</div>
            <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} style={{ width: "100%" }} />
          </div>
        </div>
        <br />
        <button onClick={sendRequest} disabled={sending}>
          {sending ? "Sending..." : "Send"}
        </button>
      </fieldset>

      <fieldset>
        <legend>Response {responseStatus !== null && `(HTTP ${responseStatus})`}</legend>
        <pre style={{ background: "#f5f5f5", padding: 12, overflowX: "auto", minHeight: 100 }}>{responseBody}</pre>
      </fieldset>

      <fieldset style={{ marginTop: 16 }}>
        <legend>Data browser (businesses / contracts / users / access codes)</legend>
        <button onClick={refreshDataBrowser}>Refresh</button>
        <span style={{ marginLeft: 8, color: "#666" }}>{browserStatus}</span>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
          <div>
            <h4>Businesses ({businesses.length})</h4>
            <DataTable
              rows={businesses}
              columns={[
                { key: "id", label: "id" },
                { key: "name", label: "name" },
                { key: "code", label: "code" },
                { key: "type", label: "type" },
              ]}
            />
          </div>
          <div>
            <h4>Contracts ({contracts.length})</h4>
            <DataTable
              rows={contracts}
              columns={[
                { key: "id", label: "id" },
                { key: "owner", label: "owner (business id)" },
                { key: "reference", label: "reference" },
                { key: "use_limit", label: "use_limit" },
                { key: "licence_count", label: "licence_count" },
              ]}
            />
          </div>
          <div>
            <h4>Users ({users.length})</h4>
            <DataTable
              rows={users}
              columns={[
                { key: "id", label: "id" },
                { key: "email", label: "email" },
                { key: "role_type", label: "role_type" },
                { key: "app_user_id", label: "app_user_id" },
              ]}
            />
          </div>
          <div>
            <h4>Access Codes ({accessCodes.length})</h4>
            <DataTable
              rows={accessCodes}
              columns={[
                { key: "id", label: "id" },
                { key: "code", label: "code" },
                { key: "codeType", label: "codeType" },
                { key: "useCount", label: "useCount" },
                { key: "useLimit", label: "useLimit" },
                { key: "validated", label: "validated" },
              ]}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default V2Playground;
